import { IConfigAPI, IConfigCache, IConfigCacheName } from '../../interface/config';
import { IMediaManager } from '../../interface/media';
import { IAuthManager } from './auth.if';
import { getMedia, requestURL as getMediaURL } from '../../private/api/media';
import { IWorkspaceDB } from '../../interface/db';
import type ILanguageDto from '../../model/LanguageDto.if';
import IMediumDto from '../../model/MediumDto.if';

interface IMediaManagerPrv extends IMediaManager {

}

function _cache(config: IConfigCache): Promise<Cache> {
    return new Promise(async function(resolve, reject) {
        try {
            if ( window.caches === undefined ) {
                reject('No caches available');
                return;
            }

            const cacheName = config[IConfigCacheName.WorkspaceMedia].cacheName;

            const cache = await caches.open(cacheName);

            resolve(cache);
        } catch (error) {
            reject(error);
        }
    });
}

function _cachedResult(mediaId: string, configAPI: IConfigAPI, configCache: IConfigCache, cache?: Cache): Promise<Blob> {
    return new Promise(async function(resolve, reject) {
        try {
            if ( !cache ) {
                cache = await _cache(configCache);
            }

            const expectedUrl =  configAPI.url + configAPI.extension.media.replace('%MEDIAID%', mediaId);

            const request = new Request(expectedUrl);
            const response = await cache.match(request, {
                ignoreMethod: true,
                ignoreSearch: true,
            })

            const blob = response ? await response.blob() : undefined;

            if ( blob ) {
                resolve(blob);
            } else {
                reject(`Unable to find id: ${mediaId} in cache`);
            }
        } catch (error) {
            reject(error);
        }
    });
}

function _downloadLink(mediaId: string, configAPI: IConfigAPI): string {
    return getMediaURL(configAPI, mediaId)
}

function _downloadLinks(mediaIds: Array<string>, configAPI: IConfigAPI): Array<string> {
    return mediaIds.map(id => _downloadLink(id, configAPI));
}

function _downloadToBlob(mediaId: string, cacheOnly: boolean, database: IWorkspaceDB, auth: IAuthManager, configAPI: IConfigAPI, configCache: IConfigCache): Promise<Blob> {
    return new Promise(async function(resolve, reject) {
        try {
            await database.open();

            /* do this first, as if it doesn't exist, lets crash out early before network requests */
            const medium = await database.medium().get(mediaId);
            let blob: Blob|null;

            if ( cacheOnly ) {
                blob = await _cachedResult(mediaId, configAPI, configCache);
            } else {
                const token = auth.hasExpired() ? await auth.renew() : auth.accessToken;
                
                const mediaBlob = await getMedia(token, mediaId);

                /* make sure to specify the mime-type in the blob, otherwise media such as mp3/mp4's won't play on Safari */
                blob = new Blob([mediaBlob], { type: medium.mimeType });
            }

            database.close();

            resolve(blob);
        } catch (error) {
            reject(error);
        }
    });
}

function _downloadToBlobs(mediaIds: Array<string>, cacheOnly: boolean, database: IWorkspaceDB, auth: IAuthManager, configAPI: IConfigAPI, configCache: IConfigCache): Promise<Array<Blob>> {
    return new Promise(async function(resolve, reject) {
        try {
            const promises = mediaIds.map(id => _downloadToBlob(id, cacheOnly, database, auth, configAPI, configCache));

            const results = Promise.all(promises);
    
            resolve(results);                
        } catch (error) {
            reject(error);
        }
    });
}

function _downloadToUrl(mediaId: string, cacheOnly: boolean, database: IWorkspaceDB, auth: IAuthManager, configAPI: IConfigAPI, configCache: IConfigCache): Promise<string> {
    return new Promise(function(resolve, reject) {
        _downloadToBlob(mediaId, cacheOnly, database, auth, configAPI, configCache)
            .then(blob => {
                const url = URL.createObjectURL(blob);
                resolve(url);
            })
            .catch(error => reject(error));
    });
}

function _downloadToUrls(mediaIds: Array<string>, cacheOnly: boolean, database: IWorkspaceDB, auth: IAuthManager, configAPI: IConfigAPI, configCache: IConfigCache): Promise<Array<string>> {
    return new Promise(function(resolve, reject) {
        _downloadToBlobs(mediaIds, cacheOnly, database, auth, configAPI, configCache)
            .then(blobs => {
                const urls = blobs.map(blob => URL.createObjectURL(blob));
                resolve(urls);
            })
            .catch(error => reject(error));
    });
}

function _isAllCached(mediaIds: Array<string>, configAPI: IConfigAPI, configCache: IConfigCache): Promise<boolean> {
    return new Promise(function(resolve, reject) {
        _cache(configCache)
            .then(cache => {
                return Promise.all(mediaIds.map(mediaId => _cachedResult(mediaId, configAPI, configCache, cache)))
            })
            .then(_ => resolve(true))
            .catch(error => resolve(false));
    });
}

function _mediaUsedForLanguage(db: IWorkspaceDB, language: ILanguageDto): Promise<Array<IMediumDto>> {
    return new Promise(async function(resolve, reject) {
        try {
            const shouldOpenClose = !db.isOpen();

            if (shouldOpenClose) {
                await db.open();
            }

            /* get a list of all media associated with SiteObjectTranslations & filter these by #language */
            const allSiteObjectTranslations = await db.siteObjectTranslation().all();
            const allMediaWithTranslations = allSiteObjectTranslations.filter(sot => (sot.mediumId)); /* if the mediumId is blank, ignore */
            const matchMediaTranslations = 
                await Promise.all(allMediaWithTranslations
                    .filter(sot => sot.languageId === language.id)
                    .map(sot => db.medium().get(sot.mediumId))) /* filter all SOTs with valid mediumId, where language matches & pull sot.mediumId from db */

            /* any media without an associated translation, treat as if it is universal (needed on all languages) & download regardless */
            const allMedia = await db.medium().all();
            const allMediaWithTranslationsIds = allMediaWithTranslations.map(m => m.mediumId);
            const mediaUniversal = allMedia.filter(medium => !allMediaWithTranslationsIds.includes(medium.id));

            if (shouldOpenClose) {
                db.close();
            }

            /* generate Mapping, so as to remove any duplicates */
            let downloadMedia: Record<string, IMediumDto> = {};

            mediaUniversal.forEach(medium => downloadMedia[medium.id] = medium);
            matchMediaTranslations.forEach(medium => downloadMedia[medium.id] = medium);

            const downloadList = Object.values(downloadMedia)

            resolve(downloadList);
        } catch (error) {
            reject(error);
        }
    });
}

function MediaManager(db: IWorkspaceDB, auth: IAuthManager, configAPI: IConfigAPI, configCache: IConfigCache): IMediaManager {
    const manager: IMediaManagerPrv = {
        downloadLink: (mediaId: string) => _downloadLink(mediaId, configAPI),
        downloadLinks: (mediaIds: Array<string>) => _downloadLinks(mediaIds, configAPI),

        downloadToURL: (id: string, cacheOnly?: boolean) => _downloadToUrl(id, cacheOnly ?? false, db, auth, configAPI, configCache),
        downloadToURLs: (ids: Array<string>, cacheOnly?: boolean) => _downloadToUrls(ids, cacheOnly ?? false, db, auth, configAPI, configCache),

        downloadToBlob: (id: string, cacheOnly?: boolean) => _downloadToBlob(id, cacheOnly ?? false, db, auth, configAPI, configCache),
        downloadToBlobs: (ids: Array<string>, cacheOnly?: boolean) => _downloadToBlobs(ids, cacheOnly ?? false, db, auth, configAPI, configCache),

        isMediumCached: (mediaId: string) => _isAllCached([mediaId], configAPI, configCache),
        isAllMediaCached: (mediaIds: Array<string>) => _isAllCached(mediaIds, configAPI, configCache),

        mediaForLanguage: (language: ILanguageDto) => _mediaUsedForLanguage(db, language),
    }
    return manager as IMediaManager;
}

export default MediaManager
